import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { translatePath } from "@/shared/i18n"
import { topUpTonXRocket } from "@/shared/api/endpoints"
import { paymentHistoryKeys } from "@/entities/history"
import { userKeys } from "@/entities/user"
import { telegram } from "@/shared/lib/telegram"
import { makeErrorToast } from "@/shared/lib/toast-error"
import {
  AnalyticsEvent,
  analyticsErrorMessage,
  classifyDepositFailure,
  getAnalyticsDepositUiSource,
  registerPendingTonDeposit,
  trackAnalyticsEvent,
} from "@/shared/lib/analytics"

export type UseXRocketDepositOptions = {
  /** e.g. close DepositSheet */
  onSuccess?: () => void
}

/**
 * xRocket deposit: API creates invoice → open t.me invoice URL inside Telegram.
 * No TonConnect wallet required.
 */
export function useXRocketDeposit(options: UseXRocketDepositOptions = {}) {
  const optsRef = useRef(options)
  optsRef.current = options

  const [isProcessing, setIsProcessing] = useState(false)
  const queryClient = useQueryClient()

  const depositViaXRocket = useCallback(
    async (amount: number) => {
      const { onSuccess } = optsRef.current
      setIsProcessing(true)
      try {
        const amountNanotons = Math.floor(amount * 1e9)
        const response = await topUpTonXRocket({ amount: amountNanotons })
        const invoiceUrl = response.payment_instructions.payload
        telegram.openTelegramLink(invoiceUrl)
        registerPendingTonDeposit("xrocket", amount)
        trackAnalyticsEvent(AnalyticsEvent.DepositInitiated, {
          deposit_method: "xrocket",
          amount_ton: amount,
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })

        queryClient.invalidateQueries({
          queryKey: paymentHistoryKeys.infinite(),
        })
        queryClient.invalidateQueries({ queryKey: userKeys.balance() })
        onSuccess?.()
      } catch (error) {
        trackAnalyticsEvent(AnalyticsEvent.DepositFailed, {
          deposit_method: "xrocket",
          amount_ton: amount,
          failure_reason: classifyDepositFailure(error),
          error_message: analyticsErrorMessage(error),
          deposit_ui_source: getAnalyticsDepositUiSource(),
        })
        const fallback = translatePath("toast.failedXRocketInvoice")
        if (error instanceof Error) {
          makeErrorToast({
            label: error.message || fallback,
          })
        } else {
          makeErrorToast({ label: fallback })
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [queryClient]
  )

  return { depositViaXRocket, isProcessing }
}
